'use client';

import * as React from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { stripHtml } from '@/lib/homepage-mappers';
import { getStrapiFormatUrl } from '@/lib/strapi-media-url';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { cn } from '@/components/ui/utils';
import type { CompanyProfileTestimonial } from '@/types/company-profile';

gsap.registerPlugin(ScrollTrigger);

interface TestimonialItem {
  id: number;
  name: string;
  role?: string;
  quote: string;
  imageUrl: string | null;
}

const DURATION = 5500;

interface CompanyProfileTestimonialsProps {
  zone: CompanyProfileTestimonial;
}

function TestimonialSlide({
  voice,
  quoteIconUrl,
}: {
  voice: TestimonialItem;
  quoteIconUrl: string | null;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[5fr_8fr] border border-black/[0.08]">
      <div className="bg-gray-50 border-b border-black/[0.08] px-4 py-8 sm:px-8 sm:py-10 md:py-12 lg:border-b-0 lg:border-r lg:px-12">
        {voice.imageUrl ? (
          <div className="relative mb-5 h-16 w-16 overflow-hidden rounded-full border border-black/10">
            <CmsImage
              src={voice.imageUrl}
              alt={voice.name}
              fill
              className="object-cover"
              sizes="64px"
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
          </div>
        ) : null}
        {voice.role ? (
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-black/45 sm:mb-3">
            {voice.role}
          </p>
        ) : null}
        <h3 className="text-[1.35rem] font-bold leading-tight tracking-tight text-black sm:text-[1.9rem] lg:text-[2.1rem]">
          {voice.name}
        </h3>
      </div>

      <div className="flex flex-col bg-white px-4 py-8 sm:px-8 sm:py-10 md:py-12 lg:px-14">
        {quoteIconUrl ? (
          <div className="relative mb-5 h-10 w-10">
            <Image
              src={quoteIconUrl}
              alt=""
              fill
              className="object-contain"
              sizes="40px"
              aria-hidden
            />
          </div>
        ) : (
          <span aria-hidden className="mb-5 block font-serif text-[3rem] leading-none text-red-600">
            &ldquo;
          </span>
        )}

        <blockquote className="text-[1rem] font-medium leading-[1.75] text-black sm:text-[1.2rem] sm:leading-[1.8] lg:text-[1.3rem]">
          {voice.quote}
        </blockquote>
      </div>
    </div>
  );
}

export default function CompanyProfileTestimonials({ zone }: CompanyProfileTestimonialsProps) {
  const sectionRef = React.useRef<HTMLElement>(null);
  const voices: TestimonialItem[] = (zone.singleTestimonial ?? [])
    .map((item) => ({
      id: item.id,
      name: item.author?.trim() || '',
      role: item.testimonialTitle?.trim() || item.aboutAuthor?.trim() || undefined,
      quote: item.content ? stripHtml(item.content) : '',
      imageUrl: getStrapiFormatUrl(item.authorImage),
    }))
    .filter((item) => item.name || item.quote);

  const title = zone.title?.trim() || '';
  const quoteIconUrl = getStrapiFormatUrl(zone.testimonialQuoteImage);
  const hasMultiple = voices.length > 1;

  const [api, setApi] = React.useState<CarouselApi>();
  const [active, setActive] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const [inView, setInView] = React.useState(false);

  React.useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.35,
      rootMargin: '0px',
    });

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!api) return;

    const onSelect = () => setActive(api.selectedScrollSnap());

    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);

    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api]);

  React.useEffect(() => {
    if (!api || paused || !inView || !hasMultiple) return;
    const t = setTimeout(() => api.scrollNext(), DURATION);
    return () => clearTimeout(t);
  }, [api, active, paused, inView, hasMultiple]);

  React.useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      gsap.set(section, { autoAlpha: 1, y: 0 });
      return;
    }

    const ctx = gsap.context(() => {
      gsap.fromTo(
        section,
        { autoAlpha: 0, y: 24 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
        },
      );
    }, section);
    return () => ctx.revert();
  }, []);

  if (voices.length === 0) return null;

  return (
    <section
      ref={sectionRef}
      className="bg-[#f3f3f3] text-black"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex flex-col gap-4 px-4 pt-5 sm:flex-row sm:items-center sm:justify-between sm:px-8 lg:px-12 mb-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
          <span className="h-px w-6 shrink-0 bg-[#ed1c22]" aria-hidden />
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#ed1c22]">
            Testimonials
          </p>
          {title ? (
            <>
              <span className="hidden text-black/20 sm:inline" aria-hidden>
                |
              </span>
              <p className="min-w-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-black/50">
                {title}
              </p>
            </>
          ) : null}
        </div>

        {hasMultiple ? (
          <div className="flex shrink-0 items-center gap-3 self-end sm:self-auto">
            <span className="font-mono text-[11px] tabular-nums text-black/45">
              {String(active + 1).padStart(2, '0')}
              <span className="text-black/25"> / </span>
              {String(voices.length).padStart(2, '0')}
            </span>
            <button
              type="button"
              onClick={() => api?.scrollPrev()}
              aria-label="Previous testimonial"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-black/15 text-black/60 transition-colors hover:border-red-600 hover:text-red-600"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => api?.scrollNext()}
              aria-label="Next testimonial"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-black/15 text-black/60 transition-colors hover:border-red-600 hover:text-red-600"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        ) : null}
      </div>

      {hasMultiple ? (
        <Carousel setApi={setApi} opts={{ align: 'start', loop: true }} className="w-full">
          <CarouselContent className="-ml-0">
            {voices.map((voice) => (
              <CarouselItem key={voice.id} className="pl-0">
                <TestimonialSlide voice={voice} quoteIconUrl={quoteIconUrl} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      ) : (
        <TestimonialSlide voice={voices[0]} quoteIconUrl={quoteIconUrl} />
      )}

      {hasMultiple ? (
        <div className="flex items-center justify-center gap-2 px-4 pb-6 pt-2 sm:px-8 lg:px-12 mt-5">
          <div className="flex items-center gap-2" role="tablist" aria-label="Testimonial slides">
            {voices.map((voice, i) => (
              <button
                key={voice.id}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-label={`Go to testimonial ${i + 1}: ${voice.name}`}
                onClick={() => api?.scrollTo(i)}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-300',
                  i === active ? 'w-8 bg-[#ed1c22]' : 'w-1.5 bg-black/50 hover:bg-black/70',
                )}
              />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
